import React, { useEffect, useMemo, useState } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { Button, DatePicker, Form, FormProps, Input, Modal, Radio } from 'antd';

import BillPDF from '../bill-pdf';
import useModal from 'stores/useModal';
import useBookingStore from 'stores/useBooking';
import useCommonStore from 'stores/useCommon';
import { useGetMe } from 'hooks/useGetMe';
import { useGetPayments } from 'hooks/useGetPayments';
import { useMutation } from '@tanstack/react-query';
import { updateInvoiceDetails } from 'services/api/module/booking.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { useGetTransactionsFromOrders } from 'hooks/useGetGuestTransactions';
import { SERVICE_TRANSACTION_CODE } from '../common/constant';
import { useTranslation } from 'react-i18next';
import NikBillPDF from '../bill-pdf/nik';

const today = dayjs();

export enum CHARGE_TYPE {
  ALL = 'all',
  ROOM = 'room',
  SERVICE = 'service'
}

function BillPDFModal() {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { isOpenViewPDF, setIsOpenViewPDF, setIsOpenSignature } = useModal();
  const { bookingLineDetail } = useBookingStore();
  const { currentBranch } = useCommonStore();

  const { data: payments } = useGetPayments(bookingLineDetail?.booking_line_id);

  const { data: transactionPayment } = useGetTransactionsFromOrders(
    bookingLineDetail?.booking_line_guest_ids?.join(',')
  );
  const { data: me } = useGetMe();

  const [dataChange, setDataChange] = useState<any>({});

  const { mutateAsync: UpdateInvoiceDetails } = useMutation({
    mutationFn: async (values: any) => {
      const id = bookingLineDetail?.booking_line_id;
      if (id === undefined) {
        return;
      }
      const data = {
        partner_name: values.partnerName || '',
        company_name: values.company || '',
        company_address: values.companyAddress || '',
        tax_code: values.taxCode || ''
      };

      const bookingDetail = await updateInvoiceDetails(id, data);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, id]
      });
      return bookingDetail;
    }
  });

  const [signatureStaff, setSignatureStaff] = useState('');
  const [signatureGuest, setSignatureGuest] = useState('');

  useEffect(() => {
    if (isOpenViewPDF && bookingLineDetail) {
      const initData = {
        chargeType: CHARGE_TYPE.ALL,
        createdDate: today,
        saleOrder: bookingLineDetail?.sale_order_name,
        partnerName: bookingLineDetail.invoice_details?.partner_name,
        company: bookingLineDetail.invoice_details?.company_name,
        companyAddress: bookingLineDetail.invoice_details?.company_address,
        taxCode: bookingLineDetail.invoice_details?.tax_code
      };
      form.setFieldsValue(initData);
      setDataChange(initData);
      setSignatureStaff('');
      setSignatureGuest('');
    }
  }, [form, bookingLineDetail, isOpenViewPDF]);

  const transactionPaymentFiltered = useMemo(() => {
    const { chargeType } = dataChange;
    if (chargeType === CHARGE_TYPE.ALL) {
      return transactionPayment?.filter(item => !item.is_deleted);
    } else if (chargeType === CHARGE_TYPE.SERVICE) {
      return transactionPayment?.filter(
        item => SERVICE_TRANSACTION_CODE.includes(item.code) && !item.is_deleted
      );
    } else {
      return transactionPayment?.filter(
        item => !SERVICE_TRANSACTION_CODE.includes(item.code) && !item.is_deleted
      );
    }
  }, [dataChange, transactionPayment]);

  const handleCancel = () => {
    setIsOpenViewPDF(false);
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    setDataChange(values);
    await UpdateInvoiceDetails(values);
  };

  const handleOpenSignatureStaff = () => {
    setIsOpenSignature(true, {
      title: t('common.signature.staff'),
      onOk: (data: any) => {
        setSignatureStaff(data);
      }
    });
  };

  const handleOpenSignatureGuest = () => {
    setIsOpenSignature(true, {
      title: t('common.signature.customer'),
      onOk: (data: any) => {
        setSignatureGuest(data);
      }
    });
  };

  return (
    <Modal
      title={t('cashierPage.previewPrint')}
      className="modal-view-bill-pdf"
      centered
      width={1200}
      open={isOpenViewPDF}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <PDFViewer width="1150" height="650" className="bill-pdf-file">
        {currentBranch?.is_nik_hotel ? (
          <NikBillPDF
            bookingLineDetail={bookingLineDetail}
            payments={payments}
            me={me}
            dataChange={dataChange}
            signatureStaff={signatureStaff}
            signatureGuest={signatureGuest}
            currentBranch={currentBranch}
            transactionPayment={transactionPaymentFiltered}
          />
        ) : (
          <BillPDF
            bookingLineDetail={bookingLineDetail}
            payments={payments}
            me={me}
            dataChange={dataChange}
            signatureStaff={signatureStaff}
            signatureGuest={signatureGuest}
            currentBranch={currentBranch}
            transactionPayment={transactionPaymentFiltered}
          />
        )}
      </PDFViewer>
      <Form
        form={form}
        name="bill-pdf-form"
        layout="vertical"
        style={{ width: '250px' }}
        initialValues={{
          chargeType: CHARGE_TYPE.ALL,
          createdDate: today,
          saleOrder: bookingLineDetail?.sale_order_name,
          roomName: bookingLineDetail?.room_name,
          partnerName: bookingLineDetail?.invoice_details?.partner_name,
          company: bookingLineDetail?.invoice_details?.company_name,
          companyAddress: bookingLineDetail?.invoice_details?.company_address,
          taxCode: bookingLineDetail?.invoice_details?.tax_code
        }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item name="chargeType" label={t('billPDFPage.payment')}>
          <Radio.Group>
            <Radio value={CHARGE_TYPE.ALL}>{t('billPDFPage.all')}</Radio>
            <Radio value={CHARGE_TYPE.ROOM}>{t('billPDFPage.roomCharge')}</Radio>
            <Radio value={CHARGE_TYPE.SERVICE}>{t('billPDFPage.serviceCharge')}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="createdDate" label={t('billPDFPage.createdDate')}>
          <DatePicker format="DD/MM/YYYY" className="w-full" />
        </Form.Item>
        <Form.Item name="partnerName" label={t('billPDFPage.customerName')}>
          <Input />
        </Form.Item>
        <Form.Item name="company" label={t('billPDFPage.company')}>
          <Input />
        </Form.Item>
        <Form.Item name="companyAddress" label={t('billPDFPage.address')}>
          <Input />
        </Form.Item>
        <Form.Item name="taxCode" label={t('billPDFPage.taxCode')}>
          <Input />
        </Form.Item>
        <Button htmlType="submit" className="ant-btn-secondary w-full">
          {t('managePage.travelAgencyPage.taActions.saveButton')}
        </Button>
        <div className="flex" style={{ flexDirection: 'column', gap: '8px', marginTop: 16 }}>
          <Button onClick={handleOpenSignatureStaff}>{t('common.signature.staff')}</Button>
          <Button onClick={handleOpenSignatureGuest}>{t('common.signature.customer')}</Button>
        </div>
      </Form>
    </Modal>
  );
}

export default BillPDFModal;
